import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { FaStepBackward } from "react-icons/fa";
import IeventType from "../../interfaces/eventtype.Interface";
import { fetchDevices, fetchEventTypes } from "../../features/Actions/ActionSlice";
import IDevice from "../../interfaces/device.Interface";
import Ievent from "../../interfaces/event.Interface";
import dayjs from "dayjs";
import Button from "../Button";
import ContentBlock from "../containers/ContentBlock";
import { useNotification } from "../../context/NotificationContext";

export default function Action() {
  const dispatch = useAppDispatch();
  const eventtypes = useAppSelector(state => state.Actions.eventtypes);
  const devices = useAppSelector(state => state.Actions.devices);
  const active_events = useAppSelector(state => state.Actions.active_events);
  const goBackState = useAppSelector(state => state.GoBack.type);
  const [output, setOutput] = useState<IeventType[]>([]);
  const [selected, setSelected] = useState<IeventType | undefined>(eventtypes.at(0));
  const [selectedDevice, setSelectedDevice] = useState<IDevice | undefined>(devices.at(0));
  const [castNumber, setCastNumber] = useState("");
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({});
  const { notify } = useNotification();
  const [lastOpenedCast, setLastOpenedCast] = useState<string | null>(null);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  

  useEffect(() => {
    dispatch(fetchEventTypes());
    dispatch({ type: "ssestream/connect", payload: "events/getActiveEvents" });
    setSelected(eventtypes.at(0));
    setSelectedDevice(devices.at(0));
  }, []);

  useEffect(() => {
    setOutput(eventtypes);
  }, [eventtypes]);

  const changeHandle = (event_id: number, handle: string, cast: string) => {
    setCastNumber(cast);
    setLastOpenedCast(cast);
    axios.post(`${process.env.REACT_APP_API_URI}/events/changeEventStatus`, {
      event_id,
      handle,
    });
    notify(cast+" state changed to: "+handle, { backgroundColor: "#00ff00", autoCloseMs: 3000 });
  };

  const goBack = () => {
    axios.post(process.env.REACT_APP_API_URI + `/goBack`, {})
  }
  const startAction = async () => {
    await axios.post(`${process.env.REACT_APP_API_URI}/events/startEvent`, {
      type: selected,
      device: selectedDevice,
    });
    notify(selected?.name+" started", { backgroundColor: "#00ff00", autoCloseMs: 3000 });
  };

  const toggleControlButtons = (cast: string) => {
    const isOpen = openPanels[cast];
    if (isOpen) {
      setOpenPanels(prev => ({ ...prev, [cast]: false }));
      setLastOpenedCast(null);
    } else {
      setOpenPanels({});
      requestAnimationFrame(() => {
        setOpenPanels({ [cast]: true });
        setLastOpenedCast(cast);
      });
    }
  };

  const getPanelStyle = (cast: string): React.CSSProperties => {
    const ref = refs.current[cast];
    return {
      maxHeight: openPanels[cast] && ref ? `${ref.scrollHeight}px` : '0px',
      transition: 'max-height 0.3s ease-in-out',
      overflow: 'hidden',
    };
  };

  useEffect(() => {
    if (!active_events || !lastOpenedCast) return;

    const match = active_events.find((e) => e.cast === lastOpenedCast);
    if (match) {
      setOpenPanels({ [lastOpenedCast]: true });
    }
  }, [active_events]);


  return (
    <>
      <ContentBlock title="Start actions">
        <div className="">
          <select
            id="action"
            className="input_text w-1/4 dark:bg-ndgray dark:border-black"
            onChange={(event) => {
              const eventype = output.find(obj => obj.name === event.target.value);
              
              setSelected(eventype);
              if (eventype) dispatch(fetchDevices(eventype.allowed_devices));
            }}
          >
            <option></option>
            {/* prev === id ? null : id */}
            {output.map((eventType) => (
              eventType.enabled != 0 ?
                <option key={eventType.id} value={eventType.name}>{eventType.name}</option>
                : ''
            ))}
          </select>

          <table>
            <tbody>
              <tr className="th-readability even:text-black">
                <td className="font-bold p-1 w-1/2">Action Name:</td>
                <td>{selected?.name}</td>
              </tr>
              <tr className="th-readability even:text-black">
                <td className="font-bold p-1 w-1/2">Action ID:</td>
                <td>{selected?.id}</td>
              </tr>
            </tbody>
          </table>

          {selected && devices.length > 0 && (
            <>
              <div>Devices:</div>
              <select
                id="device"
                className="input_text w-1/4 dark:bg-ndgray dark:border-black"
                onChange={(event) =>
                  setSelectedDevice(devices.find((obj) => obj.name === event.target.value))
                }
              >
                <option></option>
                {devices.map((device) => (
                  <option key={device.id} value={device.name}>{device.name}</option>
                ))}
              </select>
            </>
          )}

          <div className="flex justify-between items-center w-full gap-1">
            
            {(typeof selected !== 'undefined') ?
                <Button label="Start Action" width="full" onClick={
                  () => {
                    startAction();
                  }}
                />
                :
                <Button extraClasses="bg-gray-500 cursor-not-allowed hover:bg-gray-500" label="Start Action" width="full"/>
            }
            {goBackState==="event" ?
                <Button label={<FaStepBackward />} width="fit" onClick={(event) => goBack()}/>
                :
                <Button extraClasses="bg-gray-500 cursor-not-allowed hover:bg-gray-500" label={<FaStepBackward />} width="fit"/>
            }
          </div>
        </div>
      </ContentBlock>

      <ContentBlock title="Running actions">
        {active_events && (
          <table className="p-2 my-4 w-full text-center">
            <thead>
              <tr className="th-readability text-center odd:bg-tr-bg odd:text-black dark:odd:bg-black dark:odd:text-ndgray">
                <th>Action</th>
                <th>Device</th>
                <th>Cast</th>
                <th>Status</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {[...active_events]
                .sort((a, b) => a.event_type_name.localeCompare(b.event_type_name))
                .map((event_: Ievent) => {
                  if (event_.status_name !== "Failure") {
                    return (
                      <tr key={event_.cast}>
                        <td colSpan={5}>
                          <div className="event-container">
                            <div className="flex justify-around th-readability px-2 even:text-black">
                              <div className="w-1/5">{event_.event_type_name}</div>
                              <div className="w-1/5">#{event_.device_name}</div>
                              <div className="w-1/5">{event_.cast}</div>
                              <div className="w-1/5">{event_.status_name}</div>
                              <div className="w-1/5">
                                {dayjs(event_.start_date).format("YYYY-MM-DD HH:mm:ss")}
                              </div>
                            </div>

                            <div
                              id={`controlButtonDiv${event_.id}`}
                              ref={(el) => (refs.current[event_.cast] = el)}
                              className="grid control-buttons"
                              style={getPanelStyle(event_.cast)}
                            >
                              {event_.event_handle_list.split(",").map((handle) =>
                                ["Start", "End", "Failure"].includes(handle) ? null : (
                                  <Button
                                    key={handle}
                                    width="full"
                                    label={handle}
                                    onClick={() => changeHandle(event_.id, handle, event_.cast)}
                                  />
                                )
                              )}
                              <Button
                                key={"Failure"}
                                width="full"
                                label={"Failure"}
                                onClick={() => changeHandle(event_.id, "Failure", event_.cast)}
                                extraClasses="failure_button"
                              />
                              <Button
                                key={"End"}
                                width="full"
                                label={"End"}
                                onClick={() => changeHandle(event_.id, "End", event_.cast)}
                              />
                            </div>

                            <div
                              id={`closeAction${event_.id}`}
                              className={`cursor-pointer transition-transform duration-300 ${openPanels[event_.cast] ? 'rotate-180' : ''
                                }`}
                              onClick={() => toggleControlButtons(event_.cast)}
                            >
                              ▼
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  } else {
                    return null;
                  }
                })}
            </tbody>
          </table>

        )}
      </ContentBlock>

      <ContentBlock title="Failed actions">

        <table className="p-2 my-4 w-full text-center">
          <tbody>
            <tr className="th-readability text-center odd:bg-tr-bg odd:text-black dark:odd:bg-black dark:odd:text-ndgray">
              <th>Action</th>
              <th>ID</th>
              <th>Controls</th>
            </tr>
            {active_events &&
              active_events.map((event_: Ievent) => {
                if (event_.status_name === "Failure") {
                  return (
                    <tr key={event_.id} className="th-readability even:text-black">
                      <td>{event_.event_type_name}</td>
                      <td>{event_.cast}</td>
                      <td>
                        <Button
                          label="Resume"
                          width="full"
                          onClick={() => changeHandle(event_.id, "Resume", event_.cast)}
                        />
                      </td>
                    </tr>
                  );
                } else {
                  return null;
                }
              })}
          </tbody>
        </table>
      </ContentBlock>
    </>
  );
}
